import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import {
  ShieldCheck,
  Lock,
  Server,
  Key,
  Shield,
  Globe
} from 'lucide-react';

const SecurityPage = () => {
  const securityPrinciples = [
    {
      title: 'Data Encryption',
      description: 'End-to-end encryption for all data transmissions',
      icon: Lock
    },
    {
      title: 'Access Control',
      description: 'Strict authentication and authorization mechanisms',
      icon: Key
    },
    {
      title: 'Infrastructure Security',
      description: 'Secure cloud infrastructure with redundancy',
      icon: Server
    },
    {
      title: 'Compliance',
      description: 'Adherence to global security standards',
      icon: Globe
    }
  ];

  const securityMeasures = [
    {
      title: 'Network Security',
      description: 'Advanced firewall and intrusion detection systems',
      details: [
        'Multi-layered network protection',
        'Real-time threat monitoring',
        'Regular security audits'
      ]
    },
    {
      title: 'Data Protection',
      description: 'Comprehensive strategies to safeguard sensitive information',
      details: [
        'Encryption at rest and in transit',
        'Secure data storage practices',
        'Regular data integrity checks'
      ]
    },
    {
      title: 'Access Management',
      description: 'Robust identity and access control mechanisms',
      details: [
        'Multi-factor authentication',
        'Role-based access control',
        'Principle of least privilege'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Security <span className="text-primary">Commitment</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Protecting your data and AI systems with industry-leading security practices
          </p>
        </section>

        {/* Security Principles */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Security Principles</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Fundamental guidelines that drive our approach to security
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {securityPrinciples.map((principle, index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <principle.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{principle.title}</h3>
                  <p className="text-sm text-muted-foreground">{principle.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Detailed Security Measures */}
        <section className="max-w-4xl mx-auto mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Security Measures</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Advanced strategies to protect your data and AI systems
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {securityMeasures.map((measure, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    {index === 0 && <Shield className="w-6 h-6 text-primary" />}
                    {index === 1 && <Lock className="w-6 h-6 text-blue-500" />}
                    {index === 2 && <Key className="w-6 h-6 text-green-500" />}
                  </div>
                  <CardTitle>{measure.title}</CardTitle>
                  <CardDescription>{measure.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    {measure.details.map((detail, detailIndex) => (
                      <li key={detailIndex}>{detail}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Compliance and Certifications */}
        <section className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Trusted by Professionals</h2>
          <p className="text-muted-foreground mb-8">
            Our security practices meet and exceed industry standards, ensuring the highest level
            of protection for your AI systems and sensitive data.
          </p>
          <div className="flex justify-center items-center space-x-4">
            <div className="bg-white shadow-lg rounded-lg px-6 py-3">SOC 2</div>
            <div className="bg-white shadow-lg rounded-lg px-6 py-3">ISO 27001</div>
            <div className="bg-white shadow-lg rounded-lg px-6 py-3">GDPR</div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SecurityPage;
